import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  ButtonGroup,
  Link,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { ReactNode, useRef } from "react";

import { useFeedbackPushMutation } from "@/feedback/data/useFeedbackPushMutation";
import {
  NotificationProvider,
  NotificationProviderTitle,
} from "@reflag/shared/notification";

type PushFeedbackDialogProps = {
  feedbackId: string;
  provider: NotificationProvider;
  children: (props: { openDialog: () => void }) => ReactNode;
};

export default function PushFeedbackDialog({
  feedbackId,
  children,
  provider,
}: PushFeedbackDialogProps) {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const providerTitle = NotificationProviderTitle[provider];
  const {
    mutate: pushMutate,
    isPending: isPushLoading,
    data: pushData,
  } = useFeedbackPushMutation(provider, feedbackId, (data) => {
    toast({
      title: `Feedback pushed to ${providerTitle}`,
      description: (
        <Text>
          Feedback pushed to {providerTitle} successfully.{" "}
          {data.providerUrl && (
            <Link href={data.providerUrl} target="_blank">
              View it in {providerTitle}
            </Link>
          )}
        </Text>
      ),
      status: "success",
      duration: 5000,
      isClosable: true,
    });
    onClose();
  });

  const cancelRef = useRef(null);

  return (
    <>
      {children({ openDialog: onOpen })}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Push feedback</AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to push this feedback to {providerTitle}?
            </AlertDialogBody>
            <AlertDialogFooter>
              <ButtonGroup>
                <Button
                  ref={cancelRef}
                  isDisabled={isPushLoading}
                  variant="ghostDimmed"
                  onClick={onClose}
                >
                  {pushData ? "Close" : "Cancel"}
                </Button>
                <Button
                  colorScheme="blue"
                  isDisabled={!!pushData}
                  isLoading={isPushLoading}
                  onClick={() => pushMutate()}
                >
                  Push
                </Button>
              </ButtonGroup>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
